import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { MessageSquare, Users, Map, User, Film } from "lucide-react";
import { useSocial } from "@/context/SocialContext";

/**
 * Mobile bottom nav + Desktop left rail. Fixed position on mobile so
 * pages don't need extra layout logic. 5 tabs (Chats/Friends/Reels/Map/Profile).
 * Badges: pending friend requests on Friends, unread messages on Chats.
 */
const TABS = [
  { to: "/app/chats", label: "Sohbet", icon: MessageSquare, testid: "nav-chats" },
  { to: "/app/friends", label: "Dostlar", icon: Users, testid: "nav-friends" },
  { to: "/app/reels", label: "Reels", icon: Film, testid: "nav-reels" },
  { to: "/app/map", label: "Harita", icon: Map, testid: "nav-map" },
  { to: "/app/profile", label: "Profil", icon: User, testid: "nav-profile" },
];

export default function AppNav({ hideMobileWhen = false } = {}) {
  const loc = useLocation();
  const { pendingRequestCount, totalUnreadMessages } = useSocial();
  const isActive = (to) => loc.pathname === to || loc.pathname.startsWith(to + "/");

  const badgeFor = (to) => {
    if (to === "/app/friends") return pendingRequestCount;
    if (to === "/app/chats") return totalUnreadMessages;
    return 0;
  };

  return (
    <>
      {/* Mobile bottom nav */}
      <nav
        className={`${hideMobileWhen ? "hidden" : "md:hidden"} fixed bottom-0 inset-x-0 z-50 bg-white border-t-[3px] border-[#0A0A0A]`}
        data-testid="bottom-nav"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        aria-label="Ana navigasyon"
      >
        <div className="grid grid-cols-5">
          {TABS.map((t) => {
            const active = isActive(t.to);
            const Icon = t.icon;
            const badge = badgeFor(t.to);
            return (
              <NavLink
                key={t.to}
                to={t.to}
                data-testid={t.testid}
                className={`flex flex-col items-center justify-center py-2.5 gap-0.5 ${active ? "text-[#0A0A0A]" : "text-[#666]"}`}
              >
                <div className={`relative w-9 h-9 flex items-center justify-center rounded-xl border-[2px] transition-colors ${active ? "bg-[#CCFF00] border-[#0A0A0A]" : "border-transparent"}`}>
                  <Icon strokeWidth={active ? 3 : 2.25} className="w-5 h-5" />
                  {badge > 0 && (
                    <span
                      data-testid={`${t.testid}-badge`}
                      className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-[#FF3B30] text-white text-[9px] font-black flex items-center justify-center border-[2px] border-white"
                    >
                      {badge > 9 ? "9+" : badge}
                    </span>
                  )}
                </div>
                <span className={`text-[9px] uppercase tracking-tight truncate max-w-full text-center ${active ? "font-black" : "font-bold"}`}>{t.label}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>

      {/* Desktop left rail */}
      <aside
        className="hidden md:flex fixed top-0 left-0 bottom-0 z-40 w-[72px] bg-white border-r-[3px] border-[#0A0A0A] flex-col items-center gap-3 py-4"
        data-testid="left-rail"
      >
        <img
          src="/logo.svg"
          alt="Yappo"
          data-testid="app-brand-logo"
          className="w-10 h-10 mb-1"
        />
        {TABS.map((t) => {
          const active = isActive(t.to);
          const Icon = t.icon;
          const badge = badgeFor(t.to);
          return (
            <NavLink
              key={t.to}
              to={t.to}
              data-testid={`${t.testid}-rail`}
              title={t.label}
              className={`relative w-11 h-11 border-hard-2 rounded-xl flex items-center justify-center press-btn ${active ? "bg-[#CCFF00] shadow-hard-sm" : "bg-white"}`}
            >
              <Icon strokeWidth={active ? 3 : 2.5} className="w-5 h-5" />
              {badge > 0 && (
                <span
                  data-testid={`${t.testid}-rail-badge`}
                  className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF3B30] text-white text-[10px] font-black flex items-center justify-center border-[2px] border-white"
                >
                  {badge > 9 ? "9+" : badge}
                </span>
              )}
            </NavLink>
          );
        })}
        {/* Maker attribution at the very bottom of the desktop rail.
            Kept as a small vertical text link so it never distracts. */}
        <a
          href="https://tellcelltr.com"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="rail-maker-link"
          title="TELL CELL — yapımcı"
          className="mt-auto mb-1 text-[9px] font-black tracking-widest text-[#555] hover:text-[#0A0A0A] text-center leading-none press-btn"
          style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
        >
          TELL·CELL
        </a>
      </aside>
    </>
  );
}

/** Padding helper so page content clears the mobile bottom nav / desktop rail */
export const NAV_SAFE_CLASS = "pb-20 md:pb-6 md:pl-[80px]";
